import { request } from "./http";

export interface GrammarTopic {
  slug: string;
  title: string;
  summary: string | null;
  rule: string;
  examples: { de?: string; en?: string }[];
  tip: string | null;
  category: string | null;
}

export interface GrammarTable {
  slug: string;
  title: string;
  category: string;
  description: string | null;
  columns: string[];
  rows: string[][];
  /** [row, col] pairs the renderer emphasises — the endings that change. */
  highlight: number[][];
  notes: string | null;
}

export interface GrammarLevel {
  level: string;
  topics: GrammarTopic[];
  tables: GrammarTable[];
}

export interface VocabEntry {
  de: string;
  en: string;
  example: string | null;
  word_type: string | null;
  article: string | null;
  plural: string | null;
}

export interface VocabTheme {
  slug: string;
  title: string;
  entries: VocabEntry[];
}

export interface VocabLevel {
  level: string;
  themes: VocabTheme[];
  word_count: number;
}

export function referenceLevels(): Promise<string[]> {
  return request("/learning/reference/levels");
}

export function grammarForLevel(level: string): Promise<GrammarLevel> {
  return request(`/learning/reference/${level}/grammar`);
}

export function vocabForLevel(level: string): Promise<VocabLevel> {
  return request(`/learning/reference/${level}/vocab`);
}

// --- spaced repetition ------------------------------------------------------
export interface ReviewCard {
  entry_id: string;
  de: string;
  en: string;
  example: string | null;
  article: string | null;
  plural: string | null;
  /** 0 = never seen; 1–5 = Leitner box, higher means better known. */
  box: number;
  is_new: boolean;
}

export interface ReviewResult {
  box: number;
  due_on: string;
  reviews: number;
  correct: number;
  lapses: number;
}

export interface ReviewStats {
  total: number;
  started: number;
  due_today: number;
  learned: number;
  boxes: Record<string, number>;
}

export function reviewQueue(level: string, limit = 20): Promise<ReviewCard[]> {
  return request("/learning/vocab/review", { params: { level, limit } });
}

export function answerCard(entryId: string, correct: boolean): Promise<ReviewResult> {
  return request("/learning/vocab/review", {
    method: "POST",
    body: { entry_id: entryId, correct },
  });
}

export function reviewStats(level: string): Promise<ReviewStats> {
  return request("/learning/vocab/stats", { params: { level } });
}

// --- Können-Check -----------------------------------------------------------
export interface SkillScore {
  skill: string;
  /** null until the score has been EARNED — see the backend's evidence rule. */
  score: number | null;
  band: "needsWork" | "onTrack" | "strong" | null;
  evidence: number;
  needed: number;
  state: "none" | "started" | "earned";
}

export interface NextAction {
  kind: "review_vocab" | "start_skill" | "finish_evidence" | "improve_skill";
  skill: string;
  count: number;
  priority: number;
}

export interface KoennenCheck {
  skills: SkillScore[];
  overall: number | null;
  overall_band: "needsWork" | "onTrack" | "strong" | null;
  earned_count: number;
  next_actions: NextAction[];
}

export function koennenCheck(level?: string): Promise<KoennenCheck> {
  return request("/learning/koennen-check", { params: { level } });
}
